import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminBookings, notes as notesApi } from '../services/bookingsApi';
import { generateAdminInvoices, listAdminInvoices, openInvoicePdf } from '../api/invoices';
import { formatDateRange } from '../utils/dateFormat';
import { io } from 'socket.io-client';

const Timeline = ({ events = [] }) => (
  <div className="list-group list-group-flush">
    {events.map((evt) => (
      <div key={evt.id} className="list-group-item">
        <div className="d-flex justify-content-between">
          <div className="fw-semibold text-capitalize">
            {evt.event_type.replace(/_/g, ' ')} {evt.to_status ? `(${evt.to_status})` : ''}
          </div>
          <small className="text-muted">{new Date(evt.created_at).toLocaleString()}</small>
        </div>
        {evt.metadata?.reason && <div className="text-muted small">Reason: {evt.metadata.reason}</div>}
      </div>
    ))}
  </div>
);

const formatStatusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');

const PriceBreakdown = ({ booking }) => {
  const totals = booking.totals_json || {};
  const hiddenBaseBefore = Number(totals.hidden_base_addon_base_amount || totals.hidden_base_discount_base_amount || 0);
  const hiddenBaseAfter = Number(totals.hidden_base_addon_after_amount || totals.hidden_base_discount_after_amount || 0);
  const hiddenPercent = Number(totals.hidden_base_addon_percent || totals.hidden_base_discount_percent || 0);
  const lines = [
    { label: 'Base Price', value: totals.base_price ?? totals.base },
    hiddenBaseBefore ? { label: 'Customer base before hidden add-on', value: hiddenBaseBefore } : null,
    hiddenBaseAfter ? { label: `Customer base after ${hiddenPercent || 15}% hidden add-on`, value: hiddenBaseAfter } : null,
    (totals.discount_amount ?? totals.discounts) ? { label: 'Discount', value: -1 * (totals.discount_amount ?? totals.discounts) } : null,
    (totals.taxable_subtotal ?? totals.subtotal) ? { label: 'Taxable Subtotal', value: totals.taxable_subtotal ?? totals.subtotal } : null,
    totals.fee_breakdown?.cleaning_fee ? { label: 'Cleaning fee', value: totals.fee_breakdown.cleaning_fee } : null,
    (totals.mandatory_fees ?? totals.fees) ? { label: 'Mandatory fees', value: totals.mandatory_fees ?? totals.fees } : null,
    (totals.gst_amount ?? totals.taxes) ? { label: 'GST', value: totals.gst_amount ?? totals.taxes } : null,
  ].filter(Boolean);
  return (
    <div className="list-group list-group-flush">
      {lines.map((line) => (
        <div key={line.label} className="list-group-item d-flex justify-content-between">
          <span>{line.label}</span>
          <span>
            {booking.currency} {Number(line.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        </div>
      ))}
      <div className="list-group-item d-flex justify-content-between fw-semibold">
        <span>Total</span>
        <span>
          {booking.currency} {Number(totals.final_payable || totals.total || booking.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  );
};

const BookingDetailPage = ({ token }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [override, setOverride] = useState({ status: '', reason: '' });
  const [newNote, setNewNote] = useState('');
  const socketRef = useRef(null);

  const loadInvoices = async () => {
    if (!token) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const res = await listAdminInvoices({ bookingId: id, limit: 20 }, token);
      setInvoices(res.data || []);
    } catch (err) {
      if (err?.status === 404) {
        setInvoices([]);
        setInvoiceError('');
        return;
      }
      setInvoiceError(err.message || 'Failed to load invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminBookings.get(id, token);
      setData(res);
      await loadInvoices();
    } catch (err) {
      setError(err.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const socket = io(API_BASE, { transports: ['websocket'], withCredentials: true });
    socketRef.current = socket;
    socket.emit('join_booking', id);
    socket.on('booking_event_admin', (evt) => {
      if (evt?.booking_id && String(evt.booking_id) === String(id)) {
        load();
      }
    });
    socket.on('booking_note_admin', (note) => {
      if (note?.booking_id && String(note.booking_id) === String(id)) {
        load();
      }
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doOverride = async () => {
    if (!override.status) return;
    setActionLoading(true);
    try {
      await adminBookings.overrideStatus(id, override.status, override.reason, token);
      await load();
      setOverride({ status: '', reason: '' });
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote) return;
    setActionLoading(true);
    try {
      await notesApi.add(id, newNote, false, token);
      setNewNote('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateInvoices = async () => {
    if (!token) return;
    setInvoiceGenerating(true);
    setInvoiceError('');
    try {
      await generateAdminInvoices(id, token);
      await loadInvoices();
    } catch (err) {
      setInvoiceError(err.message || 'Failed to generate invoices');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const handleOpenInvoicePdf = async (invoiceId) => {
    if (!token) return;
    try {
      await openInvoicePdf(invoiceId, token);
    } catch (err) {
      setInvoiceError(err.message || 'Failed to open invoice PDF');
    }
  };

  const formatInvoiceAmount = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return `${currency || ''} ${amount || ''}`.trim();
    return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border spinner-border-sm" role="status" /> Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Booking {data.booking_code}</h4>
          <small className="text-muted">
            {formatDateRange(data.check_in_date, data.check_out_date)} - Guests {data.guests_count || data.no_of_guests || '-'}
          </small>
        </div>
        <div className="d-flex gap-2">
          <span className="badge text-bg-secondary text-capitalize">{formatStatusLabel(data.booking_status)}</span>
          <span className="badge text-bg-warning text-capitalize">{formatStatusLabel(data.payment_status)}</span>
          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => navigate('/admin/bookings')}>
            Back
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title">Guest</h6>
              <div className="fw-semibold">{data.customer_name || data.guest?.name}</div>
              <div className="text-muted">{data.customer_email || data.guest?.email}</div>
              <div className="text-muted">{data.customer_phone || data.guest?.phone}</div>
              <hr />
              <h6 className="card-title">Property</h6>
              <div className="fw-semibold">{data.property?.title || '-'}</div>
              <div className="text-muted">{data.property?.city}</div>
              <hr />
              <h6 className="card-title">Host</h6>
              <div className="text-muted">{data.host?.full_name || '-'}</div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title">Financials</h6>
              <PriceBreakdown booking={data} />
              <div className="mt-3">
                <h6 className="card-title">Payments</h6>
                {(data.payments || []).map((p) => {
                  const gatewayPaymentId = p.gateway_payment_id || p.gatewayPaymentId;
                  const gatewayOrderId = p.gateway_order_id || p.gatewayOrderId;
                  return (
                    <div key={p.id} className="border-bottom py-2 small">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">{p.provider || p.method || 'payment'}</span>
                        <span>
                          {p.currency} {p.amount} ({p.status})
                        </span>
                      </div>
                      {(gatewayPaymentId || gatewayOrderId) && (
                        <div className="text-muted">
                          {gatewayPaymentId && <div>Gateway payment: {gatewayPaymentId}</div>}
                          {gatewayOrderId && <div>Gateway order: {gatewayOrderId}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!data.payments?.length && <div className="text-muted small">No payments recorded.</div>}
              </div>
              {data.payout && (
                <div className="mt-3">
                  <h6 className="card-title">Payout</h6>
                  <div className="text-muted small">
                    {data.payout.currency} {data.payout.amount} â€¢ {data.payout.status}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title">Status override</h6>
              <div className="d-flex gap-2 mb-2">
                <select
                  className="form-select form-select-sm"
                  value={override.status}
                  onChange={(e) => setOverride((o) => ({ ...o, status: e.target.value }))}
                >
                  <option value="">Select next status</option>
                  {['pending', 'awaiting_payment', 'confirmed', 'checked_in', 'checked_out', 'completed', 'cancelled', 'declined'].map(
                    (s) => (
                      <option key={s}>{s}</option>
                    )
                  )}
                </select>
              </div>
              <textarea
                className="form-control form-control-sm mb-2"
                placeholder="Reason (required for audit)"
                value={override.reason}
                onChange={(e) => setOverride((o) => ({ ...o, reason: e.target.value }))}
              />
              <button className="btn btn-sm btn-primary" type="button" disabled={actionLoading} onClick={doOverride}>
                Save status
              </button>

              <hr />
              <h6 className="card-title">Add internal note</h6>
              <textarea
                className="form-control form-control-sm mb-2"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Note visible to admins"
              />
              <button className="btn btn-sm btn-outline-secondary" type="button" disabled={actionLoading} onClick={addNote}>
                Add note
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-2">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Timeline</h6>
              <Timeline events={data.events || []} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Notes</h6>
              {(data.notes || []).map((n) => (
                <div key={n.id} className="border-bottom py-2">
                  <div className="fw-semibold small">Visible to host: {n.visible_to_host ? 'Yes' : 'No'}</div>
                  <div className="text-muted small">{n.note}</div>
                  <div className="text-muted small">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
              {!data.notes?.length && <div className="text-muted small">No notes yet.</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="card-title mb-1">Invoices</h6>
                  <small className="text-muted">Guest receipts, host settlements, and commission invoices.</small>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleGenerateInvoices}
                  disabled={invoiceGenerating}
                >
                  {invoiceGenerating ? 'Generating...' : 'Generate invoices'}
                </button>
              </div>

              {invoiceLoading && (
                <div className="text-muted small">Loading invoices...</div>
              )}
              {invoiceError && (
                <div className="alert alert-warning py-2">{invoiceError}</div>
              )}
              {!invoiceLoading && !invoiceError && invoices.length === 0 && (
                <div className="text-muted small">No invoices generated for this booking yet.</div>
              )}
              {!invoiceLoading && !invoiceError && invoices.length > 0 && (
                <div className="list-group list-group-flush">
                  {invoices.map((inv) => {
                    const total = inv.totals?.grandTotal ?? inv.totals?.total ?? 0;
                    return (
                      <div key={inv.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">{inv.invoiceNumber}</div>
                          <div className="text-muted small text-capitalize">
                            {formatStatusLabel(inv.invoiceType)} | {new Date(inv.issuedAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold">
                            {formatInvoiceAmount(inv.currency, total)}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenInvoicePdf(inv.id)}
                          >
                            PDF
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;




